import { shallow } from "zustand/shallow";

import useExplorerStore from "../store/store";
import {
    type FundExplorerSearch,
    type FundExplorerState,
    type GroupedData,
} from "../store/types";

export default function useIsOpen(id: string) {
    const { rawResults: searchResults, getFundById } = useExplorerStore(
        ({ getFundById, search: { rawResults } }) => ({
            rawResults,
            getFundById,
        }),
        shallow,
    );

    return searchResults.length < 10
        ? matchesSearch(searchResults, getFundById, id)
        : false;
}

function matchesSearch(
    searchResults: FundExplorerSearch["rawResults"],
    getFundById: FundExplorerState["getFundById"],
    id: string,
) {
    const regex = /^shareclass-(?<NUMID>\d+)$/;
    const isShareClassId = regex.test(id);
    const matchedShareClasses = searchResults
        .filter((r) => regex.test(r.id))
        .map((r) => r.id);
    const matchedShareClassesNumIds = matchedShareClasses
        .map((strId) => parseInt(regex.exec(strId)?.groups?.NUMID ?? ""))
        .filter((numId) => !isNaN(numId));
    const conditionSC = (scId: string) => matchedShareClasses.includes(scId);
    const conditionFund = (fundId: string) =>
        getFundById(fundId)?.data.share_classes.some((scId) =>
            matchedShareClassesNumIds.includes(scId),
        );
    return isShareClassId ? conditionSC(id) : Boolean(conditionFund(id));
}

export function useOpenShareClassGroup(group: GroupedData) {
    const { rawResults: searchResults, getFundById } = useExplorerStore(
        ({ getFundById, search: { rawResults } }) => ({
            rawResults,
            getFundById,
        }),
        shallow,
    );
    const openGroup = Object.entries(group)
        .map(([key, scIds]) => [
            key,
            scIds.some((id) => matchesSearch(searchResults, getFundById, id)),
        ])
        .filter(([, isOpen]) => isOpen);
    return openGroup ? openGroup.map(([key]) => key) : null;
}
